import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      toast.error("Erro ao enviar pedido. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#F9F6F0]">
      <div className="md:w-1/2 relative bg-[#3B2B23] text-[#F9F6F0] p-6 sm:p-10 md:p-12 lg:p-16 flex flex-col justify-between overflow-hidden min-h-[180px] md:min-h-screen">
        <div className="grain absolute inset-0 opacity-30 pointer-events-none" />
        <div className="relative z-10 flex items-center gap-3 sm:gap-4">
          <img src="/unlimited-logo.png" alt="Unlimited Studio" className="w-10 h-10 sm:w-14 sm:h-14 object-contain bg-[#F9F6F0] rounded-full p-1.5 sm:p-2" />
          <div>
            <div className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#C07C5B]">Unlimited Studio</div>
            <div className="text-[9px] sm:text-[10px] tracking-wider text-[#E6DED6]/70 mt-0.5 sm:mt-1">Strategic Branding &amp; Communication</div>
          </div>
        </div>
        <div className="relative z-10 fade-up hidden md:block">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-tight">
            Recupero o<br />
            <span className="italic text-[#C07C5B]">acesso ao meu planner.</span>
          </h1>
        </div>
        <div className="relative z-10 text-[10px] tracking-wider text-[#E6DED6]/60 uppercase hidden md:block">
          © {new Date().getFullYear()} — Unlimited Studio
        </div>
      </div>

      <div className="md:w-1/2 flex items-center justify-center p-6 sm:p-10 md:p-12 lg:p-16 flex-1">
        <div className="w-full max-w-md fade-up">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#5C4A43] hover:text-[#3B2B23] mb-8"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Voltar
          </Link>
          <h2 className="text-3xl sm:text-4xl text-[#1A1412]">Esqueci-me da palavra-passe</h2>

          {sent ? (
            <div className="mt-8 p-6 rounded-2xl border border-[#EAE3DB] bg-white" data-testid="forgot-success">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#2C543B] mb-2">Pedido enviado</div>
              <p className="text-sm text-[#1A1412] leading-relaxed font-light">
                Se o email <strong>{email}</strong> existir na nossa base de dados, vai receber um link de
                recuperação nos próximos minutos. O link é válido durante 1 hora.
              </p>
              <p className="mt-4 text-xs text-[#5C4A43] font-light">
                Não recebeu? Verifique o spam ou tente novamente.
              </p>
              <Button
                onClick={() => navigate("/login")}
                className="mt-6 w-full h-11 bg-[#3B2B23] hover:bg-[#2A1E18] text-[#F9F6F0] rounded-full text-xs tracking-[0.3em] uppercase"
                data-testid="go-to-login-button"
              >
                Voltar ao login
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="forgot-form">
              <p className="mt-3 text-[#5C4A43] text-sm font-light">
                Indico o meu email e envio-me um link para definir uma nova palavra-passe.
              </p>
              <div className="mt-10 space-y-5">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Email</Label>
                  <Input
                    required type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nome@empresa.pt"
                    className="mt-2 h-12 bg-white border-[#EAE3DB] focus-visible:ring-[#8B5E3C]"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-[#3B2B23] hover:bg-[#2A1E18] text-[#F9F6F0] rounded-full text-xs tracking-[0.3em] uppercase"
                  data-testid="forgot-submit-button"
                >
                  {loading ? "A enviar…" : "Enviar link"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
